import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2, Upload } from 'lucide-react';

interface Ad {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  link_url: string | null;
  is_active: boolean;
  display_order: number;
}

export function AdManager() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  
  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    link_url: '',
    image_file: null as File | null,
  });

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setAds(data || []);
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les publicités',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setNewAd({ ...newAd, image_file: e.target.files[0] });
    }
  };

  const handleCreateAd = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAd.image_file) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une image',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      // Upload image to storage
      const fileExt = newAd.image_file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('ads')
        .upload(filePath, newAd.image_file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('ads')
        .getPublicUrl(filePath);

      // Create ad record
      const { error: insertError } = await supabase
        .from('ads')
        .insert({
          title: newAd.title,
          description: newAd.description || null,
          image_url: publicUrl,
          link_url: newAd.link_url || null,
          is_active: true,
          display_order: ads.length,
        });

      if (insertError) throw insertError;

      toast({
        title: 'Succès',
        description: 'Publicité créée avec succès',
      });

      // Reset form
      setNewAd({
        title: '',
        description: '',
        link_url: '',
        image_file: null,
      });

      // Reload ads
      loadAds();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const toggleAdStatus = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('ads')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Statut mis à jour',
      });

      loadAds();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteAd = async (id: string, imageUrl: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette publicité ?')) {
      return;
    }

    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const filePath = urlParts[urlParts.length - 1];

      // Delete from storage
      await supabase.storage.from('ads').remove([filePath]);

      // Delete from database
      const { error } = await supabase
        .from('ads')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Publicité supprimée',
      });

      loadAds();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Créer une publicité</CardTitle>
          <CardDescription>
            Ajoutez une nouvelle publicité avec image et texte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAd} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={newAd.title}
                onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newAd.description}
                onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="link_url">Lien URL (optionnel)</Label>
              <Input
                id="link_url"
                type="url"
                placeholder="https://example.com"
                value={newAd.link_url}
                onChange={(e) => setNewAd({ ...newAd, link_url: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="image">Image *</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required
              />
            </div>

            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer la publicité
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Publicités existantes ({ads.length})</CardTitle>
          <CardDescription>
            Gérez vos publicités actives
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {ads.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune publicité créée
              </p>
            ) : (
              ads.map((ad) => (
                <div key={ad.id} className="border rounded-lg p-4 flex items-start gap-4">
                  <img
                    src={ad.image_url}
                    alt={ad.title}
                    className="w-32 h-32 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{ad.title}</h3>
                    {ad.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {ad.description}
                      </p>
                    )}
                    {ad.link_url && (
                      <a
                        href={ad.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline mt-1 inline-block"
                      >
                        {ad.link_url}
                      </a>
                    )}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={ad.is_active}
                          onCheckedChange={() => toggleAdStatus(ad.id, ad.is_active)}
                        />
                        <span className="text-sm">
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteAd(ad.id, ad.image_url)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
